import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { 
  Clock, Ship, Package, DollarSign, FileText, User, 
  Calendar, Edit, Plus, Trash2, Check, AlertCircle, RefreshCw
} from 'lucide-react';

const ACTION_ICONS = {
  created: Plus,
  updated: Edit,
  deleted: Trash2,
  approved: Check,
  rejected: AlertCircle,
  registered: User,
  status_changed: RefreshCw,
};

const ENTITY_ICONS = {
  shipment: Ship,
  container: Package,
  cost: DollarSign,
  event: Calendar,
  note: FileText,
  user: User,
  organization: User,
};

const ACTION_COLORS = {
  created: 'text-green-400',
  updated: 'text-blue-400',
  deleted: 'text-red-400',
  approved: 'text-green-400',
  rejected: 'text-red-400',
  registered: 'text-purple-400',
};

export default function AuditHistoryPanel({ mbl }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAuditLogs();
  }, [mbl]);

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/shipments/${mbl}/audit`);
      setLogs(res.data);
    } catch (err) {
      console.error('Error loading audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || Edit;
    return Icon;
  };

  const getEntityIcon = (entityType) => {
    const Icon = ENTITY_ICONS[entityType] || FileText;
    return Icon;
  };

  const formatValue = (val) => {
    if (val === null || val === undefined) return '(vacío)';
    if (typeof val === 'object') {
      // For complex objects, show a summary or stringify
      return JSON.stringify(val).substring(0, 100) + (JSON.stringify(val).length > 100 ? '...' : '');
    }
    return String(val);
  };

  const formatChanges = (changes) => {
    if (!changes) return null;
    
    return Object.entries(changes).map(([field, value]) => {
      if (value && typeof value === 'object' && value.old !== undefined && value.new !== undefined) {
        const oldVal = formatValue(value.old);
        const newVal = formatValue(value.new);
        return (
          <div key={field} className="text-xs text-muted-foreground">
            <span className="font-medium">{field}:</span>{' '}
            <span className="line-through text-red-400/70">{oldVal}</span>
            {' → '}
            <span className="text-green-400/70">{newVal}</span>
          </div>
        );
      }
      return null;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">No hay historial de cambios</p>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="audit-history-panel">
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
        
        {logs.map((log, index) => {
          const ActionIcon = getActionIcon(log.action);
          const EntityIcon = getEntityIcon(log.entity_type);
          const actionColor = ACTION_COLORS[log.action] || 'text-muted-foreground';
          
          return (
            <div key={log.id} className="relative pl-10 pb-4" data-testid={`audit-log-${index}`}>
              {/* Timeline dot */}
              <div className={`absolute left-2 w-5 h-5 rounded-full bg-secondary border-2 border-border flex items-center justify-center ${actionColor}`}>
                <ActionIcon size={10} />
              </div>
              
              {/* Content */}
              <div className="bg-card border border-border rounded-sm p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <EntityIcon size={14} className="text-muted-foreground" />
                    <span className="font-medium text-sm">{log.description}</span>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDate(log.created_at)}
                  </span>
                </div>
                
                <div className="text-xs text-muted-foreground mb-1">
                  Por: {log.user_name || log.user_email}
                </div>
                
                {/* Changes detail */}
                {log.changes && Object.keys(log.changes).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border space-y-1">
                    {formatChanges(log.changes)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
